import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useMatches, useLocation, Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useAuthStore } from '@/stores/auth';
import { useRealtimeInvalidation } from '@/realtime/useRealtimeInvalidation';
import { useIsMobile } from '@/lib/responsive';
import { installColumnResize } from '@/lib/table-column-resize';
import { SECTION_ACCENT, sectionForPath } from './section-accent';

interface RouteHandle {
  title?: string;
  subtitle?: string;
}

export function AppShell() {
  useRealtimeInvalidation();
  const user = useAuthStore((s) => s.user);
  const matches = useMatches();
  const location = useLocation();
  const active = matches
    .map((m) => m.handle as RouteHandle | undefined)
    .reverse()
    .find((h): h is RouteHandle => !!h?.title);

  // 모바일 반응형 (docs/plan/windows-responsive-mobile-2026-07 §1단계).
  // 좁은 화면(<=640px)에서는 고정 사이드바가 화면의 절반을 먹으므로, 사이드바를
  // 오버레이 드로어로 숨기고 TopBar 햄버거로 연다.
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);

  // 라우트가 바뀌면(=페이지 이동) 드로어를 닫는다. 데스크톱 폭으로 넓어지면
  // 열린 상태가 남지 않게 함께 닫는다.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!isMobile) setNavOpen(false);
  }, [isMobile]);

  // 2026-10-21 오너 — 표 머리글을 사이드바 묶음 색으로(globals.css main[data-section] thead th),
  // 머리글 칸 사이를 끌어 열 폭 조절(lib/table-column-resize).
  const section = sectionForPath(location.pathname);
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!mainRef.current || isMobile) return;
    return installColumnResize(mainRef.current);
  }, [isMobile]);

  return (
    // 100dvh/100% — 모바일 주소창이 뷰포트를 먹었다 뱉는 상황에서 하단 잘림/점프를
    // 막는다(100vh 는 주소창을 포함한 큰 값이라 어긋난다). width 는 100vw 대신 100%
    // 로 두어 세로 스크롤바 폭만큼의 미세 가로 넘침을 없앤다.
    <div style={{ display: 'flex', height: '100dvh', width: '100%', overflow: 'hidden' }}>
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          top: -40,
          left: 0,
          background: 'var(--bg-surface)',
          padding: '8px 16px',
          zIndex: 9999,
          borderRadius: '0 0 4px 0',
          fontWeight: 600,
          textDecoration: 'none',
          color: 'var(--fg-default)',
          transition: 'top 0.1s',
        }}
        onFocus={(e) => (e.currentTarget.style.top = '0')}
        onBlur={(e) => (e.currentTarget.style.top = '-40px')}
      >
        본문으로 바로가기
      </a>

      {/* 데스크톱: 고정 사이드바. 모바일: 햄버거로 여는 오버레이 드로어. */}
      {!isMobile && <Sidebar role={user?.role ?? 'VIEWER'} />}
      {isMobile && navOpen && (
        <>
          <div
            onClick={() => setNavOpen(false)}
            aria-hidden
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50 }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="탐색 메뉴"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              zIndex: 51,
              animation: 'nav-drawer-slide-in 180ms ease-out',
              boxShadow: '2px 0 16px rgba(0,0,0,0.25)',
            }}
          >
            <Sidebar role={user?.role ?? 'VIEWER'} onNavigate={() => setNavOpen(false)} />
          </div>
        </>
      )}

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-canvas)',
          minWidth: 0,
        }}
      >
        <TopBar
          title={active?.title ?? 'Beyond Honeycomb Console'}
          subtitle={active?.subtitle}
          onMenuClick={isMobile ? () => setNavOpen(true) : undefined}
        />
        <main
          id="main-content"
          ref={mainRef}
          data-section={section ?? undefined}
          style={
            {
              flex: 1,
              overflow: 'auto',
              padding: isMobile ? 14 : 24,
              minHeight: 0,
              ...(section ? { '--section-accent': SECTION_ACCENT[section] } : {}),
            } as CSSProperties
          }
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
